export interface StaircaseConfig {
  min: number;
  max: number;
  step: number;
  /** Correct responses required for one upward step. Defaults to 2. */
  correctToIncrease?: number;
}

export interface StaircaseState {
  value: number;
  consecutiveCorrect: number;
  lastDirection: -1 | 0 | 1;
  reversals: number[];
  trials: number;
}

function assertConfig(config: StaircaseConfig): void {
  if (
    !Number.isFinite(config.min) ||
    !Number.isFinite(config.max) ||
    config.max < config.min ||
    !Number.isFinite(config.step) ||
    config.step <= 0 ||
    (config.correctToIncrease !== undefined &&
      (!Number.isInteger(config.correctToIncrease) || config.correctToIncrease < 1))
  ) {
    throw new RangeError('invalid staircase configuration');
  }
}

export function clampDifficulty(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

export function createStaircase(initial: number, config: StaircaseConfig): StaircaseState {
  assertConfig(config);
  return {
    value: clampDifficulty(initial, config.min, config.max),
    consecutiveCorrect: 0,
    lastDirection: 0,
    reversals: [],
    trials: 0,
  };
}

/** Pure 2-down/1-up update. Higher values are always treated as harder. */
export function updateStaircase(
  state: Readonly<StaircaseState>,
  correct: boolean,
  config: StaircaseConfig,
): StaircaseState {
  assertConfig(config);
  const required = config.correctToIncrease ?? 2;
  const consecutiveCorrect = correct ? state.consecutiveCorrect + 1 : 0;
  const direction: -1 | 0 | 1 = !correct ? -1 : consecutiveCorrect >= required ? 1 : 0;
  const nextValue =
    direction === 0
      ? state.value
      : clampDifficulty(state.value + direction * config.step, config.min, config.max);
  const moved = nextValue !== state.value;
  const reversed = moved && state.lastDirection !== 0 && direction !== state.lastDirection;

  return {
    value: nextValue,
    consecutiveCorrect: direction === 1 ? 0 : consecutiveCorrect,
    lastDirection: moved ? direction : state.lastDirection,
    reversals: reversed ? [...state.reversals, state.value] : [...state.reversals],
    trials: state.trials + 1,
  };
}

export const updateTwoDownOneUp = updateStaircase;

export function estimateStaircaseThreshold(
  state: Readonly<StaircaseState>,
  lastReversals = 6,
): number {
  const sample = state.reversals.slice(-Math.max(1, lastReversals));
  if (sample.length === 0) return state.value;
  return sample.reduce((sum, value) => sum + value, 0) / sample.length;
}

/** Block-level adaptation used by non-threshold modules. Changes one variable only. */
export function adaptBlockDifficulty(
  current: number,
  accuracy: number,
  options: { min: number; max: number; step: number; lowerTarget?: number; upperTarget?: number },
): number {
  const lower = options.lowerTarget ?? 0.75;
  const upper = options.upperTarget ?? 0.85;
  if (!Number.isFinite(accuracy) || accuracy < 0 || accuracy > 1 || lower > upper) {
    throw new RangeError('invalid block accuracy or target range');
  }
  const change = accuracy > upper ? options.step : accuracy < lower ? -options.step : 0;
  return clampDifficulty(current + change, options.min, options.max);
}
